import { faAngleDown, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from 'lodash';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import VoxelButton from '../../../../../../voxel/Buttons/VoxelButton';
import VoxelChip from '../../../../../../voxel/Chips/VoxelChip';
import VoxelDialog from '../../../../../../voxel/Dialogs/VoxelDialog';
import VoxelDialogContent from '../../../../../../voxel/Dialogs/VoxelDialogContent';
import VoxelSelectItem from '../../../../../../voxel/Forms/VoxelSelectItem';
import { VoxelPopover } from '../../../../../../voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../../../../voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../../../../voxel/Popover/VoxelPopoverTrigger';
import VoxelText from '../../../../../../voxel/Text/VoxelText';
import VoxelDivider from '../../../../../../voxel/VoxelDivider';
import { projectsApiService } from '../../../../../../../graphql/projects/projectsApiService';
import { ProjectGq } from '../../../../../../../graphql/projects/models/ProjectGq';
import VoxelReadMore from '../../../../../../voxel/Text/VoxelReadMore';
import { SearchRequestTypes } from '../AddBasicKeywords/logic/SearchModels';

export interface ChooseComboViewProps {
  project: ProjectGq;
  handleClose: () => void;
  setComboId: React.Dispatch<React.SetStateAction<number>>;
}

export const ChooseComboView: React.FC<ChooseComboViewProps> = ({
  project,
  handleClose,
  setComboId,
}) => {
  // Get combo data
  const combosQuery = projectsApiService.queries.googleAds.useGetComboRequests(
    { projectId: project.id },
    {
      pollInterval: 5000,
    }
  );
  const comboLists = combosQuery?.result;
  // Handle create combination
  // It's a bit unique since we create it and then navigate to it
  const createMutation = projectsApiService.mutations.googleAdsCombo.useCreateComboList();
  const [creating, setCreating] = useState(false);
  const createCombo = useCallback(() => {
    setCreating(true);
    createMutation({ projectId: project.id })
      .then((comboRequest) => setComboId(comboRequest.id))
      .finally(() => setCreating(false));
  }, [project.id]);

  return (
    <VoxelDialog open onClose={handleClose} title={SearchRequestTypes.find((sr) => sr.type === 'COMBO')?.name}>
      <VoxelDialogContent classMinWidth="min-w-[560px] flex flex-col gap-2">
        <VoxelReadMore
          originalText={SearchRequestTypes.find((sr) => sr.type === 'COMBO')?.shortDescription}
          readMoreText={SearchRequestTypes.find((sr) => sr.type === 'COMBO')?.moreDescription}
        />
        <div />
        <VoxelDivider />
        <div />
        <div />
        <div className="w-full flex flex-row gap-4">
          <VoxelPopover>
            <VoxelPopoverTrigger enableActive>
              <VoxelButton
                disabled={!combosQuery?.result?.length}
                title="Previous combination"
                fullWidth
                endIcon={<FontAwesomeIcon icon={faAngleDown} />}
                startIcon={<FontAwesomeIcon icon={faAngleDown} className="opacity-0" />}
              />
            </VoxelPopoverTrigger>
            <VoxelPopoverContent sourceWidth>
              <div className="max-h-[240px] overflow-y-scroll">
                {orderBy(comboLists, (c) => c.googleAdsRequest.createdTime, 'desc')?.map(
                  (comboRequest) => (
                    <VoxelSelectItem
                      key={comboRequest.id}
                      active={false}
                      selected={false}
                      onClick={() => setComboId(comboRequest.id)}
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-row gap-1 items-center">
                          <VoxelChip title={comboRequest.status} color="neutral" size="xs" />
                          <VoxelText variant="bread-normal">
                            {comboRequest.googleAdsRequest.name?.length
                              ? comboRequest.googleAdsRequest.name
                              : 'Unnamed combination'}
                          </VoxelText>
                        </div>
                        <VoxelText variant="form-caption" color="slight">
                          Created{' '}
                          {moment
                            .utc(comboRequest.googleAdsRequest.createdTime)
                            .local()
                            .format('yyyy-MM-DD')}
                        </VoxelText>
                      </div>
                    </VoxelSelectItem>
                  )
                )}
              </div>
            </VoxelPopoverContent>
          </VoxelPopover>
          <VoxelButton
            title="New combination"
            fullWidth
            endIcon={<FontAwesomeIcon icon={faPlus} />}
            startIcon={<FontAwesomeIcon icon={faPlus} className="opacity-0" />}
            color="primary"
            onClick={createCombo}
            submitting={creating}
          />
        </div>
      </VoxelDialogContent>
    </VoxelDialog>
  );
};

export default ChooseComboView;
